import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Search, X } from '@/lib/icons'
import type { BloggerListItem } from '@ayub/shared'
import { formatNumber } from '@ayub/shared'
import { fetchBloggers } from '@/components/bloggers/api'
import type { BloggerListResponse } from '@/components/bloggers/api'
import { useDebouncedValue } from '@/components/bloggers/hooks'
import { StageBadge } from '@/components/bloggers/StageBadge'
import { Button } from '@/components/ui/button'
import { Modal } from '@/components/ui/modal'
import { Skeleton } from '@/components/ui/skeleton'
import { ManualPickerEmptyState } from './EmptyStates'
import { BroadcastRetryState } from './ErrorStates'
import { Pagination } from './Pagination'

export interface ManualBloggerPickerModalProps {
  open: boolean
  /** Oldingi (avval boshqa ochilishda) tanlanganlar — oyna qayta ochilganda saqlanib qoladi. */
  initialSelectedIds: ReadonlySet<number>
  initialSelectedNames: ReadonlyMap<number, string>
  onConfirm: (ids: Set<number>, names: Map<number, string>) => void
  onClose: () => void
}

/**
 * "Kimga → Qo'lda tanlash" (`docs/03-app-flow.md` Sahifa 5) — vazifa ta'rifidagi eng murakkab
 * qism. Loyihada qayta ishlatiladigan "bloger tanlagich" komponenti yo'q edi, shuning uchun
 * mavjud `GET /api/bloggers` (`components/bloggers/api.ts`, o'zgartirilmagan) ustiga YENGIL
 * qidiruv + checkbox ro'yxati sifatida qurildi — to'liq yangi ko'p qatlamli komponent
 * EMAS, faqat `BloggerToolbar`/`ReadyRow` naqshlarining shu yerga moslashtirilgan nusxasi.
 *
 * Tashqi shartnoma (`initialSelectedIds`/`initialSelectedNames`/`onConfirm(ids, names)`)
 * ID va ismni IKKI ALOHIDA strukturada kutadi (`BroadcastPage.tsx`, bu faylning egasi emas
 * — o'zgartirilmaydi) — chunki sahifalash tufayli boshqa sahifadagi tanlangan bloger
 * ekrandan g'oyib bo'lganda ham UNING ISMINI "Tanlanganlar" chipida ko'rsatish kerak
 * (faqat ID yetarli emas).
 *
 * ICHKI holat esa ATAYLAB BITTA manba — `Map<id, fullName>` (`selectedMap`), Set + Map
 * ikkita ALOHIDA `useState`sifatida EMAS. ESKI koddagi bag aynan shu ikkilikdan kelib
 * chiqqan edi: `toggle()` ikkita mustaqil `setSelected`/`setNames` chaqirardi, ikkinchisi
 * (`setNames`) render closure'dagi ESKI `selected`ni o'qirdi — tez ketma-ket ikki marta
 * bosilganda (checkbox ustida ikki marta klik) ikkinchi chaqiruv birinchisi hali qo'llanmagan
 * eski holatga asoslanib qaror qabul qilardi, natijada `names`da ID `selected`dan chiqib
 * ketgandan keyin ham qolib ketishi mumkin edi. Bitta `Map` bilan har ikkala ma'lumot
 * (bor-yo'qligi VA ismi) BITTA atomik funksional yangilanishda o'zgaradi — ikkinchi manba
 * bilan solishtirish umuman yo'q.
 */

/**
 * Ikki tashqi manbani (`initialSelectedIds` + `initialSelectedNames`) ichki `Map`ga
 * birlashtiradi — faqat IKKALASIDA ham bor ID'lar olinadi (`initialSelectedIds` orqali
 * filtrlanadi). Amalda ota komponent ularni har doim BIRGA, bir xil to'plamdan yozadi
 * (`BroadcastPage.tsx`), lekin ikkita mustaqil prop bo'lgani uchun kelajakda ular
 * assinxron holga kelib qolishi (masalan biri yangilanib, ikkinchisi eskirgan) mumkin —
 * shu filtr shunday holatda ismi noma'lum "yarim" yozuv paydo bo'lishining oldini oladi.
 */
function mergeInitialSelection(
  ids: ReadonlySet<number>,
  names: ReadonlyMap<number, string>,
): Map<number, string> {
  return new Map(Array.from(names).filter(([id]) => ids.has(id)))
}

export function ManualBloggerPickerModal({
  open,
  initialSelectedIds,
  initialSelectedNames,
  onConfirm,
  onClose,
}: ManualBloggerPickerModalProps) {
  const [selectedMap, setSelectedMap] = useState<Map<number, string>>(() =>
    mergeInitialSelection(initialSelectedIds, initialSelectedNames),
  )
  const [searchInput, setSearchInput] = useState('')
  const [page, setPage] = useState(1)
  const q = useDebouncedValue(searchInput, 300)

  // Oyna har ochilganda oldingi (tashqaridan kelgan) tanlov bilan qayta boshlanadi —
  // "Bekor qilish" bosilsa o'zgarishlar saqlanmasligi uchun (mahalliy nusxa bilan ishlanadi,
  // faqat "Tanlashni yakunlash" bosilganda `onConfirm` orqali tashqariga chiqariladi).
  useEffect(() => {
    if (!open) return
    setSelectedMap(mergeInitialSelection(initialSelectedIds, initialSelectedNames))
    setSearchInput('')
    setPage(1)
    // Diqqat: bog'liqlik ro'yxati ATAYLAB faqat `[open]` — `initialSelectedIds`/`initialSelectedNames`
    // qo'shilsa, ota komponent har render safar YANGI `Set`/`Map` obyekti yuborgani uchun
    // (referens tengsizligi) bu effekt HAR renderda ishga tushib, foydalanuvchi hali oyna
    // ichida bosgan checkboxlarni doimiy ravishda tashqi holatga qaytarib yuborardi.
  }, [open])

  const query = useQuery<BloggerListResponse, Error>({
    queryKey: ['bloggers', 'manualPicker', q, page],
    queryFn: () => fetchBloggers({ q: q.length > 0 ? q : undefined, page, pageSize: 20 }),
    enabled: open,
  })

  // BITTA atomik funksional yangilanish — bor-yo'qligi VA ismi bir vaqtda, bitta `prev`dan
  // hisoblanadi, ikkinchi (eskirgan bo'lishi mumkin) state manbaiga qaramaydi.
  function toggle(item: BloggerListItem) {
    setSelectedMap((prev) => {
      const next = new Map(prev)
      if (next.has(item.id)) next.delete(item.id)
      else next.set(item.id, item.fullName)
      return next
    })
  }

  const items = query.data?.items ?? []
  const total = query.data?.total ?? 0

  return (
    <Modal open={open} onClose={onClose} ariaLabel="Blogerlarni qo'lda tanlash" zIndex={50}>
      <div className="flex shrink-0 items-center justify-between border-b border-app-border px-5 py-4">
        <h2 className="text-base font-semibold">Blogerlarni tanlash</h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Yopish"
          className="flex size-11 items-center justify-center rounded-button text-app-hint hover:bg-app-card"
        >
          <X size={18} aria-hidden="true" />
        </button>
      </div>

      <div className="flex shrink-0 flex-col gap-2 border-b border-app-border px-5 py-3">
        <div className="relative">
          <Search
            size={16}
            className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-app-hint"
            aria-hidden="true"
          />
          <input
            type="text"
            inputMode="search"
            value={searchInput}
            onChange={(event) => {
              setSearchInput(event.target.value)
              setPage(1)
            }}
            placeholder="Qidirish: ism, @username, telefon..."
            aria-label="Blogerlarni qidirish"
            className="h-11 w-full rounded-input border border-app-border bg-app-bg pr-3 pl-9 text-base text-app-text placeholder:text-app-hint outline-none focus-visible:border-brand"
          />
        </div>
        <p className="text-xs text-app-hint">
          <span className="tabular font-medium text-app-text">
            {formatNumber(selectedMap.size)} ta
          </span>{' '}
          tanlangan
          {selectedMap.size > 0 && (
            <button
              type="button"
              onClick={() => setSelectedMap(new Map())}
              className="ml-2 font-medium text-brand underline underline-offset-2"
            >
              Tozalash
            </button>
          )}
        </p>
      </div>

      {/* `aria-busy` — skeleton skrinrider uchun jim, shu bilan qo'shimcha signal beriladi
          (ro'yxat hozir eskirgan/yangilanmoqda). */}
      <div className="flex-1 overflow-y-auto px-3 py-2" aria-busy={query.isFetching}>
        {query.isLoading ? (
          <div className="flex flex-col gap-2 p-2">
            {Array.from({ length: 6 }, (_, index) => (
              <Skeleton key={index} className="h-14" />
            ))}
          </div>
        ) : query.isError ? (
          // ⚠️ MUHIM TUZATISH: avval bu yerda `isError` tekshirilmagan edi — tarmoq
          // uzilsa yoki server 500 qaytarsa `data` `undefined` bo'lib, `items = []` orqali
          // "Hali birorta bloger ro'yxatdan o'tmagan" degan YOLG'ON bo'sh holat ko'rsatilardi
          // (aslida blogerlar bor, so'rov yiqilgan). Endi xato holati aniq ajratiladi va
          // qayta urinish imkoniyati beriladi.
          <BroadcastRetryState
            error={query.error}
            onRetry={() => void query.refetch()}
            className="min-h-0 py-8"
          />
        ) : items.length === 0 ? (
          <ManualPickerEmptyState
            message={
              q.length > 0
                ? `«${q}» bo'yicha bloger topilmadi`
                : "Hali birorta bloger ro'yxatdan o'tmagan"
            }
          />
        ) : (
          <ul className="flex flex-col gap-1">
            {items.map((item) => (
              <li key={item.id}>
                <label className="flex min-h-11 cursor-pointer items-center gap-3 rounded-button px-2 py-2 hover:bg-app-card">
                  <input
                    type="checkbox"
                    checked={selectedMap.has(item.id)}
                    onChange={() => toggle(item)}
                    aria-label={`${item.fullName} — tanlash`}
                    className="size-4 shrink-0 accent-brand"
                  />
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate font-medium text-app-text">{item.fullName}</span>
                    <span className="truncate text-xs text-app-hint">
                      @{item.instagramUsername}
                    </span>
                  </span>
                  <StageBadge stage={item.stage} className="shrink-0" />
                </label>
              </li>
            ))}
          </ul>
        )}
        {total > 20 && (
          <Pagination page={page} pageSize={20} total={total} onPageChange={setPage} />
        )}
      </div>

      <div className="flex shrink-0 justify-end gap-2 border-t border-app-border px-5 py-4">
        <Button variant="secondary" onClick={onClose}>
          Bekor qilish
        </Button>
        <Button
          onClick={() => onConfirm(new Set(selectedMap.keys()), new Map(selectedMap))}
          disabled={selectedMap.size === 0}
        >
          Tanlashni yakunlash
          {selectedMap.size > 0 ? ` (${formatNumber(selectedMap.size)})` : ''}
        </Button>
      </div>
    </Modal>
  )
}
